import styled from 'styled-components';
import { baseVariables, toRem } from '../../styles';
import { MContentHeaderAlignment } from '../../molecules/MContentHeader/MContentHeader.types';

const { mediaQuery } = baseVariables;

/** Matches OCardStandard box-shadow blur radius (1.25rem). */
const CARD_SHADOW_BLEED = '1.25rem';

export const Wrapper = styled.div<{ $peeking?: boolean }>`
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  display: flex;
  justify-content: center;
  padding-block: var(--density-mobile, ${toRem(24)});
  padding-inline: var(--layout-xs-mobile-portrait-outside-padding-margin, ${toRem(16)});
  background-color: var(--t-brand-bg-color);
  color: var(--t-brand-fg-color);

  @media ${mediaQuery.md} {
    padding-block: var(--density-tablet, ${toRem(32)});
    padding-inline: 0;
  }

  @media ${mediaQuery.lg} {
    padding-block: var(--density-desktop, ${toRem(32)});
  }

  @media ${mediaQuery.xl} {
    overflow-x: ${({ $peeking }) => ($peeking ? 'visible' : 'hidden')};
  }
`;

export const HeaderWrapper = styled.div<{ $alignment?: MContentHeaderAlignment }>`
  width: 100%;
  padding-block-end: var(--general-spacer-04, ${toRem(24)});

  /* MContentHeader ships with its own bottom padding; avoid stacking it with HeaderWrapper + carousel gap. */
  [data-testid='web-ui-kit-MContentHeader'] {
    padding-block-end: 0;
  }

  ${({ $alignment }) =>
    $alignment === MContentHeaderAlignment.Centered
      ? `
        [data-testid='web-ui-kit-MContentHeader'] {
          align-items: center;
          text-align: center;
        }

        [data-testid='web-ui-kit-MTextBlockSimple'] {
          text-align: center;
        }

        [data-testid='web-ui-kit-AEyebrow'] {
          text-align: center;
          display: unset;
        }
      `
      : ''}

  ${({ $alignment }) =>
    !$alignment || $alignment === MContentHeaderAlignment.Left
      ? `
        [data-testid='web-ui-kit-MContentHeader'] {
          align-items: flex-start;
        }

        @media ${mediaQuery.md} {
          [data-testid='web-ui-kit-MContentHeader'] {
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;

            /*
             * MContentHeader TextBlockWrapper uses flex: 1 1 343px for side-by-side CTA layout.
             * When HeaderWrapper stacks the header vertically at tablet+, that basis becomes height
             * and leaves a large empty area below short header copy.
             */
            > * {
              flex: 0 1 auto;
            }
          }
        }
      `
      : ''}

  @media ${mediaQuery.xl} {
    min-width: 0;
    padding-block-end: 0;
  }
`;

export const CarouselSection = styled.div<{ $peeking?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--general-spacer-03, ${toRem(16)});

  @media ${mediaQuery.xl} {
    width: ${({ $peeking }) =>
      $peeking ? `calc(${toRem(752)} + max(0px, (100vw - ${toRem(1140)}) / 2))` : toRem(752)};
    overflow: ${({ $peeking }) => ($peeking ? 'visible' : 'hidden')};
  }
`;

export const CarouselContainer = styled.div<{ $peeking?: boolean }>`
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--layout-xl-desktop-gutter, ${toRem(24)});

  @media ${mediaQuery.md} {
    width: ${toRem(720)};
    max-width: 100%;
  }

  @media ${mediaQuery.xl} {
    width: ${toRem(1140)};
    display: grid;
    grid-template-columns: minmax(0, 1fr) ${toRem(752)};
    align-items: center;
    column-gap: var(--layout-xl-desktop-gutter, ${toRem(24)});
    overflow: ${({ $peeking }) => ($peeking ? 'visible' : 'hidden')};
  }
`;

export const SwiperWrapper = styled.div<{ $peeking?: boolean }>`
  /*
   * Mobile: bleed only into the right Wrapper padding so the next card peeks at the
   * viewport edge while the first card stays aligned with the header (16px inset).
   */
  margin-inline-end: calc(-1 * var(--layout-xs-mobile-portrait-outside-padding-margin, ${toRem(16)}));
  width: calc(100% + var(--layout-xs-mobile-portrait-outside-padding-margin, ${toRem(16)}));
  max-width: calc(100% + var(--layout-xs-mobile-portrait-outside-padding-margin, ${toRem(16)}));
  overflow: hidden;

  @media ${mediaQuery.md} {
    /*
     * Tablet: extend past the 720px content column so the peeking card reaches the
     * viewport right edge (Wrapper overflow-x clips any excess, no page scroll).
     */
    margin-inline-end: 0;
    width: calc(100% + ${toRem(32)});
    max-width: calc(100% + ${toRem(32)});
  }

  @media ${mediaQuery.lg} {
    width: calc(100% + var(--o-card-carousel-combo-desktop-peek-width, ${toRem(88)}));
    max-width: calc(100% + var(--o-card-carousel-combo-desktop-peek-width, ${toRem(88)}));
  }

  @media ${mediaQuery.xl} {
    margin-inline-end: 0;
    width: 100%;
    max-width: 100%;
  }

  .swiper {
    width: 100%;
    height: auto;
    overflow: hidden;
    box-sizing: border-box;
    /* Reserve space inside the clip box so card shadows are not cut off. */
    padding-block-end: ${CARD_SHADOW_BLEED};
    padding-inline: ${CARD_SHADOW_BLEED};
    /* Prevent pre-init slide stacking before Swiper applies transforms. */
    opacity: 0;
    transition: opacity 150ms ease;

    &.swiper-initialized {
      opacity: 1;
    }

    @media ${mediaQuery.xl} {
      overflow: ${({ $peeking }) => ($peeking ? 'visible' : 'hidden')};
      /* Two full cards + gutter exactly fill 752px; only add side inset when peeking. */
      padding-inline: ${({ $peeking }) => ($peeking ? CARD_SHADOW_BLEED : '0')};
    }
  }

  .swiper-slide {
    height: auto;
    display: flex;
    align-items: stretch;
    justify-content: flex-start;
    box-sizing: border-box;
    flex-shrink: 0;
    /*
     * !important beats Swiper's default width: 100% before init and inline widths
     * after init when using slidesPerView: 'auto'.
     */
    width: var(--o-card-carousel-combo-card-width-mobile, ${toRem(283)}) !important;

    @media ${mediaQuery.sm} {
      width: var(--o-card-carousel-combo-card-width-sm, ${toRem(308)}) !important;
    }

    @media ${mediaQuery.md} {
      width: var(--o-card-carousel-combo-card-width-tablet, ${toRem(286)}) !important;
    }

    @media ${mediaQuery.lg} {
      justify-content: flex-start;
      width: var(--o-card-carousel-combo-card-width-lg, ${toRem(304)}) !important;
    }

    @media ${mediaQuery.xl} {
      width: var(--o-card-carousel-combo-card-width-desktop, ${toRem(364)}) !important;
    }
  }

  .swiper-wrapper {
    align-items: stretch;
    display: flex;
    flex-wrap: nowrap;
  }

  .swiper:not(.swiper-initialized) .swiper-wrapper {
    gap: var(--general-spacer-03, ${toRem(16)});

    @media ${mediaQuery.md} {
      gap: var(--layout-xl-desktop-gutter, ${toRem(24)});
    }
  }

  .glide__slides_authoring {
    overflow: visible;
    opacity: 1 !important;

    .swiper-wrapper {
      flex-wrap: wrap;
      transform: none !important;
      gap: var(--layout-xl-desktop-gutter, ${toRem(24)});
    }

    .swiper-slide {
      width: 45% !important;
    }
  }
`;

export const SlideContent = styled.div<{ $peeking?: boolean }>`
  width: 100%;
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: visible;

  /*
   * Equal-height slides: stretch wrapper chain to the card shell only.
   * Card content keeps its natural spacing; extra height shows as empty space
   * below the content, not as stretched gaps between text/CTAs.
   */
  .m-slot,
  .m-slot > *,
  .m-slot > * > *,
  [data-component-name='o-card-standard'],
  & > [data-component-name='o-web-ui-kit-OCardStandard'] {
    flex: 1 1 auto;
    width: 100% !important;
    min-width: 0;
    min-height: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  [data-component-name='o-web-ui-kit-OCardStandard'] {
    height: 100%;
    min-height: 100%;
    justify-content: flex-start;

    > * {
      flex: 0 0 auto;
    }
  }

  .aem-container {
    padding-inline: 0 !important;
  }
`;

export const ControlsWrapper = styled.div<{ $imageOverlay?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-block-start: ${({ $imageOverlay }) => ($imageOverlay ? '0' : `var(--general-spacer-03, ${toRem(16)})`)};
  padding-inline: 0;
  position: ${({ $imageOverlay }) => ($imageOverlay ? 'absolute' : 'relative')};
  inset-block-end: ${({ $imageOverlay }) => ($imageOverlay ? `var(--general-spacer-04, ${toRem(24)})` : 'auto')};
  inset-inline-start: ${({ $imageOverlay }) => ($imageOverlay ? '50%' : 'auto')};
  transform: ${({ $imageOverlay }) => ($imageOverlay ? 'translateX(-50%)' : 'none')};
  z-index: ${({ $imageOverlay }) => ($imageOverlay ? '10' : 'auto')};
`;
